import { AGENT_PHASES, type AgentPhase } from "./agentPhases";
import { type AgentLabelSources, entityLabel, isEntityPresent } from "./entityLabel";
import type {
    AgentDetailGroup,
    AgentDetailItem,
    AgentEntityRef,
    AgentReportData,
    AgentStepReport,
    ReportLine,
} from "./runner/types";
import type { MessageId } from "@domain/intl/useTranslation";

type Translate = (id: MessageId, values?: Record<string, string | number>) => string;

export const DETAIL_GROUP_LABELS: Record<AgentDetailGroup, MessageId> = {
    done: "app.agent.details.group.done",
    needsYou: "app.agent.details.group.needsYou",
    refused: "app.agent.details.group.refused",
    untouched: "app.agent.details.group.untouched",
};

/** Resolves the label placeholders of a report line with `t` and formats it. */
export const reportLineText = (t: Translate, line: ReportLine): string => {
    const labels = Object.fromEntries(Object.entries(line.labels ?? {}).map(([key, id]) => [key, t(id)]));
    return t(line.textId, { ...line.values, ...labels });
};

export const formatDuration = (t: Translate, ms: number): string => {
    const total = Math.floor(ms / 1000);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return minutes > 0
        ? t("app.agent.report.durationMinutes", { minutes, seconds })
        : t("app.agent.report.durationSeconds", { seconds });
};

/** The header after "Fertig": step count with duration, the counts per status, and the mode. */
export const reportHeaderParts = (t: Translate, report: AgentReportData): string[] => {
    const { counts } = report;
    const steps = counts.done + counts.nothing + counts.skipped + counts.failed;
    return [
        t("app.agent.report.stepsIn", { count: steps, duration: formatDuration(t, report.durationMs) }),
        t("app.agent.report.counts", { ...counts }),
        t("app.agent.report.modeLine", {
            mode: t(report.mode === "quick" ? "app.agent.mode.quick" : "app.agent.mode.approval"),
        }),
    ];
};

/** The one-word result of a step for the report sections. */
export const stepStatusText = (t: Translate, report: AgentStepReport): string => {
    switch (report.status) {
        case "done":
            return t("app.agent.report.stepDone");
        case "nothing":
            return t("app.agent.stepList.nothing");
        case "failed":
            return t("app.agent.stepList.failed");
        case "skipped":
            return t(report.skipReason === "noRight" ? "app.agent.stepList.noRight" : "app.agent.stepList.skipped");
    }
};

export interface ReportPhase {
    phase: AgentPhase;
    steps: AgentReportData["steps"];
}

/** The recorded steps grouped by phase; phases without a recorded step are left out. */
export const reportPhases = (report: AgentReportData): ReportPhase[] =>
    AGENT_PHASES.map((phase) => ({
        phase,
        steps: report.steps.filter((s) => phase.steps.includes(s.id)),
    })).filter((p) => p.steps.length > 0);

/** The before/after rows worth showing: kinds with work at the start or at the end. */
export const visibleBuckets = (report: AgentReportData) => report.buckets.filter((b) => b.before > 0 || b.after > 0);

/** A detail label for the download; an entity outside the loaded data is "nicht geladen", never "deleted". */
const downloadLabel = (t: Translate, ref: AgentEntityRef, sources: AgentLabelSources): string =>
    isEntityPresent(ref, sources) ? entityLabel(ref, sources, t, false) : t("app.agent.details.notLoaded");

const detailText = (t: Translate, item: AgentDetailItem, sources: AgentLabelSources): string => {
    const entity = item.ref
        ? [downloadLabel(t, item.ref, sources), ...(item.target ? [downloadLabel(t, item.target, sources)] : [])].join(
              " → ",
          )
        : undefined;
    return [t(DETAIL_GROUP_LABELS[item.group]), entity, reportLineText(t, item.outcome)].filter(Boolean).join(": ");
};

/** The report as plain text: header, todo, before/after, both line sections and every step's details. */
export const reportToPlainText = (
    t: Translate,
    report: AgentReportData,
    company: string | undefined,
    sources: AgentLabelSources,
): string => {
    const section = (heading: string, lines: string[], emptyId: MessageId) => [
        heading,
        ...(lines.length > 0 ? lines.map((l) => `- ${l}`) : [`- ${t(emptyId)}`]),
        "",
    ];
    const text = (lines: ReportLine[]) => lines.map((l) => reportLineText(t, l));
    const buckets = visibleBuckets(report);
    const steps = reportPhases(report).flatMap(({ phase, steps: list }) => [
        t(phase.labelId),
        ...list.flatMap(({ id, report: step }) => [
            `  ${t(`app.agent.step.${id}`)}: ${stepStatusText(t, step)}`,
            ...step.details.map((item) => `  - ${detailText(t, item, sources)}`),
        ]),
    ]);
    return [
        [t("app.agent.title"), company, String(report.year)].filter(Boolean).join(" - "),
        [t("app.agent.report.finished"), ...reportHeaderParts(t, report)].join(" · "),
        "",
        ...section(
            t("app.agent.report.todoTitle"),
            text(report.todo.map((i) => i.line)),
            "app.agent.report.nothingOpen",
        ),
        ...(buckets.length > 0
            ? [
                  t("app.agent.report.bucketsTitle"),
                  ...buckets.map((b) => `- ${t(`app.agent.report.bucket.${b.kind}`)}: ${b.before} → ${b.after}`),
                  "",
              ]
            : []),
        ...section(t("app.agent.report.sectionDone"), text(report.done), "app.agent.report.nothingDone"),
        ...section(t("app.agent.report.sectionOpen"), text(report.open), "app.agent.report.nothingOpen"),
        ...(steps.length > 0 ? [t("app.agent.report.stepsTitle"), ...steps, ""] : []),
    ].join("\n");
};
